import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useExperience } from '@/hooks/useExperience';
import { SectionLayout } from '@/components/shared/section-layout';
import { useOpenEntity } from '@/hooks/useOpenEntity';
import { NavigationService } from '@/services/navigationService';
import { Button } from '@/components/ui/button';
import { GenericTag } from '@/styles/GenericTag';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { Warranty, NewWarrantyInput } from './types';
import { useWarranties, warrantyExportRows, fetchWarrantyDerived } from './store';
import { fetchProjectLookup } from '@/lib/entity-api';
import { exportEntityCsv } from '@/lib/csv';
import { PurchaseOrderCreateDialog } from '@/features/cost-management/purchase-orders/components/PurchaseOrderCreateDialog';
import { WarrantyForm } from './components/WarrantyForm';
import { Copy, Trash2, Download } from 'lucide-react';
import { getApiUrl } from '@/lib/api-config';
import { derivedDescription } from '@/lib/derived-description';
import { WarrantyEmailService } from './warranty-email-service';

export function WarrantiesPage() {
  const { companyId, user } = useUser();
  const { can } = usePermissions();
  const clientView = useExperience() === 'client'; // a client opens claims and reads / comments on them; the rest is the company's
  const { shouldOpen, entityId } = useOpenEntity('warranty');
  const { items, addWarranty, updateWarranty, refreshWarranties, exportRows, setSearchInput, tableProps, hasLoaded } = useWarranties();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Warranty | null>(null);
  // The warranty a purchase order is being created for (the PO form opens over the warranty form).
  const [poFor, setPoFor] = useState<{ warranty: Warranty; projectId: string } | null>(null);
  // The open warranty always reflects the latest data (e.g. a PO just created from it).
  const editingLive = editing ? items.find((w) => w.id === editing.id) ?? editing : null;
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const projectIdToName = useMemo(() => new Map(projects.map((p) => [p.id, p.name] as const)), [projects]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  // Latest list for the effect that opens a record from navigation (read when it runs, never a reason to re-run it).
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (companyId && user) {
      fetchProjectLookup().then(setProjects).catch((error) => console.error('Error fetching projects:', error));
    }
  }, [companyId, user]);

  // Check if we should open a specific warranty from navigation
  // Check if we should open a specific warranty from navigation
  useEffect(() => {
    if (!shouldOpen || !entityId || !hasLoaded) return;
    // Clear the state to prevent reopening on refresh
    NavigationService.clearOpenState();
    // The warranty may not be on the page that is showing: ask the API for it (derived data included).
    const loaded = itemsRef.current.find((w) => w.id === entityId);
    (loaded ? Promise.resolve([loaded]) : fetchWarrantyDerived([entityId]))
      .then(([warranty]) => {
        if (warranty) {
          setEditing(warranty);
          setEditOpen(true);
        }
      })
      .catch((error) => console.error('Error opening warranty:', error));
  }, [shouldOpen, entityId, hasLoaded]);

  const formatAssignedTo = (warranty: Warranty) => {
    if (!warranty.assignedTo) return 'Unassigned';
    return warranty.assignedToName || warranty.assignedTo;
  };

  const formatResponseDate = (responseDate?: string) => {
    if (!responseDate) return 'No response';
    return new Date(responseDate).toLocaleDateString();
  };


  async function handleCreate(data: NewWarrantyInput) {
    return addWarranty(data);
  }

  async function handleEdit(data: NewWarrantyInput) {
    if (editing) return updateWarranty(editing.id, data);
    return undefined;
  }

  const handleSendWarranty = async (warrantyId: string, warrantyData?: Warranty) => {
    if (!user?.id || !companyId) {
      alert('User session expired. Please refresh the page.');
      return;
    }

    try {
      const result = await WarrantyEmailService.sendWarrantyEmail({
        warrantyId: warrantyId,
        companyId: companyId,
        userId: user.id,
      });

      if (result.success) {
        alert(result.message || 'Warranty sent successfully!');
        // Refresh warranties list to get updated status
        await refreshWarranties();
        // Close modals if open
        if (editOpen) {
          setEditOpen(false);
          setEditing(null);
        }
        if (createOpen) {
          setCreateOpen(false);
        }
      } else {
        alert(result.error || 'Failed to send warranty.');
      }
    } catch (error) {
      console.error('Error sending warranty:', error);
      alert('An unexpected error occurred while sending the warranty.');
    }
  };

  // "Create PO": the purchase order form opens ON TOP of the warranty form, which stays open and
  // untouched. A warranty that was still being created is already saved by now, so the page
  // continues on that record (edit mode) instead of closing anything.
  const handleCreatePO = (warranty: Warranty, projectId: string) => {
    if (createOpen) {
      setCreateOpen(false);
      setEditing(warranty);
      setEditOpen(true);
    }
    setPoFor({ warranty, projectId });
  };

  // Handle copy selected warranties
  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one warranty to copy.');
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to copy warranties');
      return;
    }

    try {
      const selectedWarranties = items.filter(w => selectedRows.includes(w.id));
      let successCount = 0;
      let errorCount = 0;

      // Copy each selected warranty - let API generate warranty_number automatically
      for (const warranty of selectedWarranties) {
        try {
          const response = await fetch(getApiUrl('api/entities/warranty'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              // Don't include warranty_number - let API generate it
              project_id: warranty.projectId,
              title: warranty.title,
              description: warranty.description || null,
              response: warranty.response || null,
              start_date: warranty.startDate || new Date().toISOString().split('T')[0],
              end_date: warranty.endDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              status: 'open', // Always set to open for copies
              assigned_to: warranty.assignedTo || null, // This is now the UUID
              created_by_type: 'company',
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error copying warranty:', errorData);
            errorCount++;
            continue;
          }
          successCount++;
        } catch (error) {
          console.error('Error copying warranty:', error);
          errorCount++;
        }
      }

      await refreshWarranties();
      setSelectedRows([]);
      if (errorCount > 0) {
        alert(`Copied ${successCount} warranty(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully copied ${successCount} warranty(s).`);
      }
    } catch (error) {
      console.error('Error copying warranties:', error);
      alert('Error copying warranties. Please try again.');
    }
  };

  // Handle delete selected warranties
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one warranty to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} warranty(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete warranties');
      return;
    }

    try {
      for (const warrantyId of selectedRows) {
        const response = await fetch(getApiUrl(`api/entities/warranty/${warrantyId}`), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error deleting warranty:', errorData);
          continue;
        }
      }

      await refreshWarranties();
      setSelectedRows([]);
      alert(`Successfully deleted ${selectedRows.length} warranty(s).`);
    } catch (error) {
      console.error('Error deleting warranties:', error);
      alert('Error deleting warranties. Please try again.');
    }
  };

  // Export to CSV: the rows selected in the table, or -- with nothing selected -- EVERY warranty of the current
  // project + search (read by the backend), with the derived columns (assigned name, ball in court, counters).
  const handleExportSelected = () => exportEntityCsv(
    async (ids) => warrantyExportRows(await exportRows(ids), (id) => projectIdToName.get(id) || id, formatAssignedTo),
    selectedRows, 'warranties', 'warranties'
  );

  // Define table columns (placed after all handler functions to avoid hoisting issues)
  const columns: GenericTableColumn<Warranty>[] = [
    {
      key: 'warrantyNumber',
      header: 'Warranty ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'warranty_number',
      render: (row) => <span className="font-medium">{row.warrantyNumber}</span>,
    },
    {
      key: 'title',
      header: 'Title',
      width: '12.5rem', // 200px = 12.5rem
      sortable: true,
      sortKey: 'title',
      render: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      key: 'projectId',
      header: 'Project',
      width: '9.375rem', // 150px = 9.375rem
      sortable: true,
      sortKey: 'project',
      render: (row) => projectIdToName.get(row.projectId) || 'Unknown Project',
    },
    {
      key: 'status',
      header: 'Status',
      width: '6.5rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => <GenericTag type="status" value={row.status} />,
    },
    {
      key: 'assignedTo',
      header: 'Assigned To',
      width: '8.125rem',
      sortable: true,
      sortKey: 'assigned_to',
      render: (row) => formatAssignedTo(row),
    },
    {
      key: 'ballInCourt',
      header: 'Ball in Court',
      width: '7.5rem',
      render: (row) => (row.ballInCourt === 'client' ? 'Client' : row.ballInCourt === 'company' ? 'Company' : '—'),
    },
    {
      key: 'createdAt',
      header: 'Created Date',
      width: '8.125rem',
      sortable: true,
      sortKey: 'created_at',
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      key: 'responseDate',
      header: 'Response Date',
      width: '8.75rem',
      sortable: true,
      sortKey: 'response_date',
      render: (row) => formatResponseDate(row.responseDate),
    },
    {
      key: 'attachments',
      header: 'Attachments',
      width: '6.875rem',
      align: 'center' as const,
      render: (row) => row.attachmentCount,
    },
    {
      key: 'commentsCount',
      header: 'Comments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.commentsCount,
    },
  ];

  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  return (
    <SectionLayout title="Warranties">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            {(!clientView || can('warranties.create')) && (
              <NewItemButton
                label={clientView ? "New Claim" : "New"}
                onClick={() => setCreateOpen(true)}
              />
            )}
            <SearchButton
              onSearch={setSearchInput}
              placeholder="Search"
            />
            {!clientView && <ThreeDotMenuButton items={menuItems} />}
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={(item) => { setEditing(item); setEditOpen(true); }}
          selectable={!clientView}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
        />

        <WarrantyForm
          mode="create"
          initialData={null}
          onCancel={() => setCreateOpen(false)}
          onSubmit={handleCreate}
          projects={projects}
          clientView={clientView}
          isOpen={createOpen}
          onSend={handleSendWarranty}
          onCreatePO={handleCreatePO}
          onChanged={refreshWarranties}
        />

        <WarrantyForm
          mode="edit"
          initialData={editingLive}
          onCancel={() => { setEditOpen(false); setEditing(null); }}
          onSubmit={handleEdit}
          projects={projects}
          clientView={clientView}
          isOpen={editOpen}
          onSend={handleSendWarranty}
          onCreatePO={handleCreatePO}
          onChanged={refreshWarranties}
        />

        {poFor && (
          <PurchaseOrderCreateDialog
            isOpen
            onClose={() => setPoFor(null)}
            defaults={poFor ? { title: `Warranty - ${poFor.warranty.warrantyNumber}`, projectId: poFor.projectId, description: derivedDescription({ type: 'Warranty', number: poFor.warranty.warrantyNumber, title: poFor.warranty.title, description: poFor.warranty.description }) } : undefined}
            warrantyId={poFor?.warranty.id}
            onCreated={(po) => {
              alert(`Purchase order ${po.poNumber} created from warranty ${poFor?.warranty.warrantyNumber}.`);
              refreshWarranties();
            }}
          />
        )}
      </div>
    </SectionLayout>
  );
} 